import React from 'react';
import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { FROST_PROBE_CONFIG, REST_API, STATE_STATUS } from '@awp/constants';
import { GetJson, PutJson, DeleteJson } from '@awp/helpers/fetchReact';
import { SENSOR_PLACEMENT_CONFIG } from '@awp/constants';
import { toNumberOrNull, isValueEmpty, isJsonValueEmpty, isJsonString } from '@awp/helpers/utilsReact';
import AwpToastContent from '@components/Common/AwpToastContent';
import { ToastContainer, toast } from 'react-toastify';

let initialState = {
  status: STATE_STATUS.IDLE,
  weatherStationId: 0,
  editing: null,
  data: {},
  observationTypes: [],
  placementTemplates: [],
  placement: null,
  orignPlacement: null,
  segment: null,
  orignSegment: null,
  saveStatus: STATE_STATUS.IDLE,
  saveError: null,
  deletePlacementStatus: STATE_STATUS.IDLE,
  deletePlacementError: null,
  saveSegmentStatus: STATE_STATUS.IDLE,
  saveSegmentError: null,
  deleteSegmentStatus: STATE_STATUS.IDLE,
  deleteSegmentError: null,
  sotId: SENSOR_PLACEMENT_CONFIG.SOT_ID,
};

export const getCurrentSensorEquipmentPacket = (state) => state.sensorEquipmentPacket.data;
export const getCurrentSensorEquipmentSets = (state) => state.sensorEquipmentPacket.data.SensorEquipmentSets;
export const getOperationStartDate = (state) => state.sensorEquipmentPacket.operationStartDate;
export const getWeatherStationId = (state) => state.sensorEquipmentPacket.weatherStationId;
export const getSelectedPlacement = (state) => state.sensorEquipmentPacket.placement;
export const getOrignPlacement = (state) => state.sensorEquipmentPacket.orignPlacement;
export const getObservationTypes = (state) => state.sensorEquipmentPacket.observationTypes;
export const getPlacementTemplates = (state) => state.sensorEquipmentPacket.placementTemplates;
export const getSensorTypes = (state) => state.sensorEquipmentPacket.sensorTypes;
export const getSensors = (state) => state.sensorEquipmentPacket.sensors;
export const getSelectedSegment = (state) => state.sensorEquipmentPacket.segment;
export const getOrignSegment = (state) => state.sensorEquipmentPacket.orignSegment;
export const getNewSotId = (state) => state.sensorEquipmentPacket.sotId;
export const isEditing = (state) => state.sensorEquipmentPacket.editing;
export const isLoading = (state) => state.sensorEquipmentPacket.status === STATE_STATUS.LOADING;
export const hasLoaded = (state) =>
  state.sensorEquipmentPacket.status === STATE_STATUS.SUCCESS ||
  state.sensorEquipmentPacket.status === STATE_STATUS.FAILED;
export const getSaveSegmentStatus = (state) => state.sensorEquipmentPacket.saveSegmentStatus;
export const getSaveSegmentError = (state) => state.sensorEquipmentPacket.saveSegmentError;
export const getDeleteSegmentStatus = (state) => state.sensorEquipmentPacket.deleteSegmentStatus;
export const getDeleteSegmentError = (state) => state.sensorEquipmentPacket.deleteSegmentError;
export const getSaveStatus = (state) => state.sensorEquipmentPacket.saveStatus;
export const getSaveError = (state) => state.sensorEquipmentPacket.saveError;
export const getDeletePlacementStatus = (state) => state.sensorEquipmentPacket.deletePlacementStatus;
export const getDeletePlacementError = (state) => state.sensorEquipmentPacket.deletePlacementError;

export const getCurrentSensorEquipmentSetFrostProbe = createSelector(
  [getCurrentSensorEquipmentSets],
  (sensorEquipmentSets) => {
    return sensorEquipmentSets?.find((sensorEquipmentSet) => {
      return sensorEquipmentSet.SensorTypeName === FROST_PROBE_CONFIG.FORST_PROBE_SENSOR_TYPE;
    });
  },
);

export const sensorPlacementItemsDropdownBySensorId = (sensorTypeId) =>
  createSelector([getCurrentSensorEquipmentPacket], (sensorEquipment) => {
    // Step 1: Filter the sensor types by SensorTypeId and get WeatherStationIds
    const filteredSensorEquipmentSets = sensorEquipment.SensorEquipmentSets?.filter(
      (equipment) => equipment.SensorTypeId === sensorTypeId,
    ).map((equipment) => {
      return { value: equipment.SensorPlacementId, label: equipment.PlacementName };
    });

    return filteredSensorEquipmentSets;
  });

export const fetchSensorEquipmentPacket = createAsyncThunk(
  'sensorEquipmentPacket/fetchSensorEquipmentPacket',
  async (weatherStationId, { getState }) => {
    //const currentWx = getState().sensorEquipmentPacket.weatherStationId;
    const url = REST_API.WEATHER_STATIONS + weatherStationId + '/sensorequipmentpacket';
    const res = await GetJson(url);
    const sensorEquipmentPacket = await res.json();
    return sensorEquipmentPacket;
  },
  {
    condition: (weatherStationId, { getState }) => {
      const state = getState();
      if (!weatherStationId || weatherStationId < 1) return false;
      //state.isensorEquipmentPacket.weatherStatonId=weatherStationId;
      if (isLoading(state) || hasLoaded(state)) return false;
      return true;
    },
  },
);

export const savePlacement = createAsyncThunk('sensorEquipmentPacket/savePlacement', async (payload, { getState }) => {
  const state = getState();
  const currentWx = state.sensorEquipmentPacket.weatherStationId;
  const url = REST_API.WEATHER_STATIONS + currentWx + '/sensorequipmentset';
  const resp = await PutJson(url, state.sensorEquipmentPacket.placement);
  if (resp.ok) {
    return { response: await resp.json() };
  }
  return { response: null, error: await resp.json() };
});

export const deletePlacement = createAsyncThunk(
  'sensorEquipmentPacket/deletePlacement',
  async (payload, { getState }) => {
    const state = getState();
    const currentWx = state.sensorEquipmentPacket.weatherStationId;
    const url = REST_API.WEATHER_STATIONS + currentWx + '/sensorequipmentset';
    const resp = await DeleteJson(url, state.sensorEquipmentPacket.placement);
    if (resp.ok) {
      return { response: await resp.json() };
    }
    return { response: null, error: await resp.json() };
  },
);

export const saveSegment = createAsyncThunk('sensorEquipmentPacket/saveSegement', async (payload, { getState }) => {
  const state = getState();
  const currentWx = state.sensorEquipmentPacket.weatherStationId;
  const url = REST_API.WEATHER_STATIONS + 'update/placementsegment';
  const resp = await PutJson(url, state.sensorEquipmentPacket.segment);
  if (resp.ok) {
    return { response: await resp.json() };
  }
  return { response: null, error: await resp.json() };
});

export const deleteSegment = createAsyncThunk('sensorEquipmentPacket/deleteSegement', async (payload, { getState }) => {
  const state = getState();
  const currentWx = state.sensorEquipmentPacket.weatherStationId;
  const url = REST_API.WEATHER_STATIONS + currentWx + '/placementsegment';

  const resp = await DeleteJson(url, state.sensorEquipmentPacket.segment);
  if (resp.ok) {
    return { response: await resp.json() };
  }
  return { response: null, error: await resp.json() };
});

export const sensorEquipmentPacketSlice = createSlice({
  name: 'sensorEquipmentPacket',
  initialState,
  reducers: {
    setWeatherStationId(state, action) {
      return { ...state, weatherStationId: action.payload };
    },
    setEditing(state, action) {
      //editing: sot+id or sp+id
      return { ...state, editing: action.payload };
    },
    setStatus(state, action) {
      return { ...state, status: action.payload };
    },
    setData(state, action) {
      return { ...state, data: action.payload };
    },
    setObservationTypes(state, action) {
      return { ...state, observationTypes: action.payload };
    },
    setPlacement(state, action) {
      return { ...state, placement: action.payload };
    },
    setOrignPlacement(state, action) {
      return { ...state, orignPlacement: action.payload };
    },
    setSotId(state, action) {
      return { ...state, sotId: action.payload };
    },
    setSegment(state, action) {
      return { ...state, segment: action.payload };
    },
    setOrignSegment(state, action) {
      return { ...state, orignSegment: action.payload };
    },
    setSaveSegmentStatus(state, action) {
      return { ...state, saveSegmentStatus: action.payload };
    },
    setSaveSegmentError(state, action) {
      return { ...state, saveSegmentError: action.payload };
    },
    setDeleteSegmentStatus(state, action) {
      return { ...state, deleteSegmentStatus: action.payload };
    },
    setDeleteSegmentError(state, action) {
      return { ...state, deleteSegmentError: action.payload };
    },
    setSaveStatus(state, action) {
      return { ...state, saveStatus: action.payload };
    },
    setSaveError(state, action) {
      return { ...state, saveError: action.payload };
    },
    setDeletePlacementStatus(state, action) {
      return { ...state, deletePlacementStatus: action.payload };
    },
    setDeletePlacementError(state, action) {
      return { ...state, deletePlacementError: action.payload };
    },
  },
  extraReducers: {
    [fetchSensorEquipmentPacket.pending.toString()]: (state) => {
      state.status = STATE_STATUS.LOADING;
    },
    [fetchSensorEquipmentPacket.fulfilled.toString()]: (state, { payload }) => {
      state.status = STATE_STATUS.SUCCESS;
      state.data = payload;

      const _data = { ...state.data };
      const _ots = [..._data.ObservationTypes];
      let _allObservationTypeOptions = [];
      _ots.map((ot) => {
        _allObservationTypeOptions.push({
          value: ot.ObservationTypeId,
          label: ot.ObservationTypeName,
          observationTypeDisplayName: ot.ObservationTypeDisplayName,
        });
      });
      state.observationTypes = _allObservationTypeOptions;

      const _sts = [..._data.SensorTypeList];
      let _allSensorTypeOptions = [];
      _sts.map((st) => {
        _allSensorTypeOptions.push({
          value: st.SensorTypeId,
          label: st.SensorTypeName,
          displayName: st.SensorTypeDisplayName,
          PlacementAttributeSchema: st.PlacementAttributeSchema,
        });
      });
      state.sensorTypes = _allSensorTypeOptions;
      state.operationStartDate = _data.OperationStartDate;

      const _sensors = [..._data.SensorList];
      let _allSensorOptions = [];
      _sensors.map((sr) => {
        _allSensorOptions.push({
          value: sr.SensorId,
          label: `${sr.SerialNumber} - ${sr.ModelNumber} - ${sr.Manufacturer}`,
          sensor: sr,
        });
      });
      state.sensors = _allSensorOptions;

      state.placementTemplates = [..._data.PlacementTemplates];
    },
    [fetchSensorEquipmentPacket.rejected.toString()]: (state) => {
      state.status = STATE_STATUS.FAILED;
    },
    [savePlacement.pending]: (state) => {
      state.saveStatus = STATE_STATUS.LOADING;
    },
    [savePlacement.fulfilled]: (state, action) => {
      const { response, error } = action.payload;
      if (!response) {
        state.saveStatus = STATE_STATUS.FAILED;

        if (error) {
          state.saveError = { ...error };
          if (error.entity) {
            toast(<AwpToastContent title={'Could not save placement !'} msgList={error.entity} />, {
              autoClose: false,
            });
            return;
          }
          toast.error('Could not save placement ! ', {
            autoClose: false,
          });
        }
        return;
      }
      state.saveStatus = STATE_STATUS.SUCCESS;
      let savedPlacement = { ...response };

      let _data = { ...state.data };
      let _sensorEquipmentSets = _data.SensorEquipmentSets;
      let _newSensorEquipmentSets = [];
      let isNew = true;
      _sensorEquipmentSets.map((sp) => {
        let _sp = { ...sp };
        if (toNumberOrNull(_sp.SensorPlacementId) === toNumberOrNull(savedPlacement.SensorPlacementId)) {
          _newSensorEquipmentSets.push(savedPlacement);
          isNew = false;
        } else {
          _newSensorEquipmentSets.push(_sp);
        }
      });

      if (isNew) {
        _newSensorEquipmentSets = []; // reload
        _sensorEquipmentSets.map((sp) => {
          let _sp = { ...sp };
          if (toNumberOrNull(_sp.SensorPlacementId) === toNumberOrNull(SENSOR_PLACEMENT_CONFIG.ID)) {
            _newSensorEquipmentSets.push(savedPlacement);
          } else {
            _newSensorEquipmentSets.push(_sp);
          }
        });
      }

      _data.SensorEquipmentSets = _newSensorEquipmentSets;
      state.data = _data;

      //cleanData
      state.editing = null;
      state.orignPlacement = null;
      state.placement = null;
      state.saveError = null;

      toast.success('Placement successfully saved!');
    },
    [savePlacement.rejected]: (state, action) => {
      state.saveStatus = STATE_STATUS.FAILED;
    },
    [deletePlacement.pending]: (state) => {
      //state.status = STATE_STATUS.LOADING;
      state.deletePlacementStatus = STATE_STATUS.LOADING;
    },
    [deletePlacement.fulfilled]: (state, action) => {
      const { response, error } = action.payload;
      if (!response) {
        state.deletePlacementStatus = STATE_STATUS.FAILED;

        if (error) {
          state.deletePlacementError = { ...error };
          if (error.entity) {
            toast(<AwpToastContent title={'Could not delete placement !'} msgList={error.entity} />, {
              autoClose: false,
            });
            return;
          }
          toast.error('Could not delete placement ! ', {
            autoClose: false,
          });
        }
        return;
      }

      state.deletePlacementStatus = STATE_STATUS.SUCCESS;
      let deletedPlacement = { ...response };
      let _data = { ...state.data };

      let _sensorEquipmentSets = _data.SensorEquipmentSets;
      let _newSensorEquipmentSets = [];
      _sensorEquipmentSets.map((sp) => {
        let _sp = { ...sp };
        if (_sp.SensorPlacementId !== deletedPlacement.SensorPlacementId) _newSensorEquipmentSets.push(_sp);
      });
      _data.SensorEquipmentSets = _newSensorEquipmentSets;
      state.data = _data;

      //cleanData
      state.editing = null;
      state.orignPlacement = null;
      state.placement = null;
      state.saveError = null;
      state.deletePlacementError = null;

      toast.success('Placment successfully deleted!');
    },
    [deletePlacement.rejected]: (state, action) => {
      state.deletePlacementStatus = STATE_STATUS.FAILED;
    },
    [saveSegment.pending]: (state) => {
      //state.status = STATE_STATUS.LOADING;
      state.saveSegmentStatus = STATE_STATUS.LOADING;
    },
    [saveSegment.fulfilled]: (state, action) => {
      const { response, error } = action.payload;
      if (!response) {
        state.saveSegmentStatus = STATE_STATUS.FAILED;

        if (error) {
          state.saveSegmentError = { ...error };
          if (error.entity) {
            toast(<AwpToastContent title={'Could not save placement segment !'} msgList={error.entity} />, {
              autoClose: false,
            });
            return;
          }
          toast.error('Could not save placement segment ! ', {
            autoClose: false,
          });
        }
        return;
      }
      state.saveSegmentStatus = STATE_STATUS.SUCCESS;
      let savedSegment = { ...response };
      let _data = { ...state.data };

      let _sensorEquipmentSets = _data.SensorEquipmentSets;
      let _newSensorEquipmentSets = [];
      _sensorEquipmentSets.map((sp) => {
        let _sp = { ...sp };
        let _newPlacementSegments = [];
        _sp.PlacementSegments.map((seg) => {
          if (seg.Id === SENSOR_PLACEMENT_CONFIG.SEG_ID || seg.Id === savedSegment.Id) {
            _newPlacementSegments.push(savedSegment);
          } else {
            _newPlacementSegments.push(seg);
          }
        });
        _sp.PlacementSegments = _newPlacementSegments;
        _newSensorEquipmentSets.push(_sp);
      });
      _data.SensorEquipmentSets = _newSensorEquipmentSets;
      state.data = _data;
      /////////////////
      //cleanData
      state.editing = null;
      state.orignSegment = null;
      state.segment = null;
      state.saveSegmentError = null;
      toast.success('Placment segment successfully saved!');
    },
    [saveSegment.rejected]: (state, action) => {
      state.saveSegmentStatus = STATE_STATUS.FAILED;
    },
    [deleteSegment.pending]: (state) => {
      //state.status = STATE_STATUS.LOADING;
      state.deleteSegmentStatus = STATE_STATUS.LOADING;
    },
    [deleteSegment.fulfilled]: (state, action) => {
      const { response, error } = action.payload;
      if (!response) {
        state.deleteSegmentStatus = STATE_STATUS.FAILED;

        if (error) {
          state.deleteSegmentError = { ...error };
          if (error.entity) {
            toast(<AwpToastContent title={'Could not delete placement segment !'} msgList={error.entity} />, {
              autoClose: false,
            });
            return;
          }
          toast.error('Could not delete placement segment ! ', {
            autoClose: false,
          });
        }
        return;
      }

      state.deleteSegmentStatus = STATE_STATUS.SUCCESS;
      let deletedSegment = { ...response };
      let _data = { ...state.data };

      let _sensorEquipmentSets = _data.SensorEquipmentSets;
      let _newSensorEquipmentSets = [];
      _sensorEquipmentSets.map((sp) => {
        let _sp = { ...sp };
        let _newPlacementSegments = [];
        _sp.PlacementSegments.map((seg) => {
          if (seg.Id !== deletedSegment.Id) _newPlacementSegments.push(seg);
        });
        _sp.PlacementSegments = _newPlacementSegments;
        _newSensorEquipmentSets.push(_sp);
      });
      _data.SensorEquipmentSets = _newSensorEquipmentSets;
      state.data = _data;

      /////////////////
      //cleanData
      state.editing = null;
      state.orignSegment = null;
      state.segment = null;
      state.deleteSegmentError = null;
      toast.success('Placment segment successfully deleted!');
    },
    [deleteSegment.rejected]: (state, action) => {
      state.deleteSegmentStatus = STATE_STATUS.FAILED;
    },
  },
});

export const getGeneralError = (err) => {
  if (isJsonValueEmpty(err)) return '';
  //check property of dict obj
  for (let key in err) {
    let k = key.toLocaleLowerCase();
    if ((k + '').includes('.generalerror')) return err[key];
  }
  return '';
};

//helper functions
export const getPlacementList = (selectedOptions, data) => {
  if (!data) return null;
  if (!data.Datasets) return null;
  if (!selectedOptions) return null;
  return data.Datasets.filter((dataset) => selectedOptions.find((option) => option.value === dataset.value));
};

//for multi-lanes json schema and update json of placement
export const getjsonObject = (_placement, sensorTypeList) => {
  let placement = { ..._placement };
  if (!placement) return null;
  let placeJsonObj =
    placement && !isValueEmpty(placement.StructuredAttributes) && isJsonString(placement.StructuredAttributes)
      ? JSON.parse(placement.StructuredAttributes)
      : null;
  if (placement.SensorPlacementId < 0) return placeJsonObj;
  const st = sensorTypeList.find((opt) => opt.value == placement.SensorTypeId);
  let stJsonObj =
    st && !isValueEmpty(st.PlacementAttributeSchema) && isJsonString(st.PlacementAttributeSchema)
      ? JSON.parse(st.PlacementAttributeSchema)
      : null;
  if (!stJsonObj || !stJsonObj.properties || stJsonObj.properties.length === 0) return null;
  if (isValueEmpty(placeJsonObj) && !isValueEmpty(stJsonObj)) return stJsonObj;
  Object.keys(stJsonObj.properties).map((p, i) => {
    if (isValueEmpty(p) || isValueEmpty(placeJsonObj) || isValueEmpty(placeJsonObj.properties)) return;
    stJsonObj.properties[p].value =
      placeJsonObj && placeJsonObj.properties[p] ? placeJsonObj.properties[p].value : null;
    if (
      !isValueEmpty(stJsonObj) &&
      !isValueEmpty(stJsonObj.properties[p]) &&
      !isValueEmpty(stJsonObj.properties[p].enum) &&
      stJsonObj.properties[p].enum.length > 0 &&
      !isValueEmpty(stJsonObj.properties[p].value) &&
      !stJsonObj.properties[p].enum.includes(stJsonObj.properties[p].value)
    )
      stJsonObj.properties[p].value = null;
  });
  return stJsonObj;
};
export const {
  setWeatherStationId,
  setStatus,
  setData,
  setEditing,
  setPlacement,
  setOrignPlacement,
  setSegment,
  setOrignSegment,
  setSotId,
  setSaveSegmentStatus,
  setSaveSegmentError,
  setDeleteSegmentStatus,
  setDeleteSegmentError,
  setSaveStatus,
  setSaveError,
  setDeletePlacementStatus,
  setDeletePlacementError,
} = sensorEquipmentPacketSlice.actions;
